import React, { useCallback, useState } from "react";
import { debounce, get } from "lodash";
import {
  Autocomplete,
  Box,
  CircularProgress,
  TextField,
  ListSubheader,
} from "@mui/material";
import { Controller } from "react-hook-form";
import { useNatureCodeHandler } from "../../resources/common/getNatureCodesHandler";

const NatureCodeSelect = ({
  label = "Nature Code",
  name,
  control,
  errors,
  helperText,
  readOnly = false,
}) => {
  const [searchTerm, setSearchTerm] = useState("");

  const modifySearchTerm = useCallback(
    debounce((value) => {
      setSearchTerm(value.trim());
    }, 300),
    [],
  );

  const { natureCodes, isLoading } = useNatureCodeHandler({
    searchQuery: searchTerm || "",
  });

  const errorMessage = get(errors, name, {})?.message;
  const combinedHelperText = [helperText, errorMessage]
    .filter(Boolean)
    .join(" * ");

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => {
        const selectedValue = (() => {
          if (!field.value) return null;
          return (
            natureCodes.find((opt) => opt.code === field.value) ?? {
              code: field.value,
            }
          );
        })();
        return (
          <Box display="flex" alignItems="flex-start" gap={2}>
            <Autocomplete
              loading={isLoading}
              name={name}
              fullWidth
              readOnly={readOnly}
              options={natureCodes || []}
              renderGroup={(params) => (
                <li key={params.key}>
                  <ListSubheader
                    sx={{
                      minHeight: "32px",
                      color: "#111111",
                      fontWeight: 600,
                      lineHeight: "24px",
                      paddingLeft: "5px",
                    }}
                  >
                    {params.group}
                  </ListSubheader>
                  {params.children}
                </li>
              )}
              getOptionLabel={(option) => {
                if (!option) return "";
                if (typeof option === "string") return option;
                return option.code || "";
              }}
              renderOption={(props, option) => {
                const { key, ...optionProps } = props;
                return (
                  <Box key={key} component="li" {...optionProps}>
                    {option.code} - {option.description}
                  </Box>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={label}
                  error={!!errorMessage}
                  helperText={combinedHelperText}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {isLoading && <CircularProgress size={20} />}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              value={selectedValue}
              onChange={(_, value) => {
                field.onChange(value ? value?.code : null);
              }}
              onInputChange={(_, value, reason) => {
                if (reason === "input") {
                  modifySearchTerm(value);
                }
              }}
              isOptionEqualToValue={(option, value) =>
                option.code === value?.code
              }
              filterOptions={(x) => x}
            />
          </Box>
        );
      }}
    />
  );
};

export default NatureCodeSelect;
